import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart, Area,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { RangeSelector, useTimeRange } from '../data/TimeRange';

// Gold primary, deep-gold min, green moving average. No cyan/blue.
const COLORS = {
  avg: '#FFB800',
  min: '#E09E00',
  movingAvg: '#10B981',
  range: '#FFC21E',
};

const SpO2Chart = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { inRange, spanDays, selectorProps } = useTimeRange('1M');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/spo2');
        if (response.status === 200) setAllData(response.data);
      } catch (error) {
        toast.error('Failed to load SpO2 data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    return allData.filter(d => inRange(d.date));
  }, [allData, inRange]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const avgVals = filteredData.filter(d => d.avg_spo2 != null).map(d => d.avg_spo2);
    const minVals = filteredData.filter(d => d.min_spo2 != null).map(d => d.min_spo2);
    const latest = filteredData[filteredData.length - 1];
    return {
      latestAvg: latest.avg_spo2,
      latestMin: latest.min_spo2,
      latestMax: latest.max_spo2,
      overallAvg: avgVals.length ? +(avgVals.reduce((a, b) => a + b, 0) / avgVals.length).toFixed(1) : null,
      overallMin: minVals.length ? Math.min(...minVals) : null,
      lowestAvg: avgVals.length ? Math.min(...avgVals) : null,
      totalEntries: filteredData.length,
    };
  }, [filteredData]);

  const chartData = useMemo(() => {
    if (!filteredData.length) return [];
    const windowSize = filteredData.length > 90 ? 14 : 7;

    return filteredData.map((d, i) => {
      const window = filteredData.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.avg_spo2 != null).map(w => w.avg_spo2);
      const movingAvg = avgVals.length >= 2
        ? +(avgVals.reduce((a, b) => a + b, 0) / avgVals.length).toFixed(1)
        : null;

      return {
        date: d.date,
        avg: d.avg_spo2,
        min: d.min_spo2,
        max: d.max_spo2,
        movingAvg,
      };
    });
  }, [filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-600 dark:text-gray-300">
            No SpO2 data available. This requires a Fitbit device with SpO2 tracking capability.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (spanDays <= 30) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const singleDay = spanDays === 1;

  const SpO2Tooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          {d.avg != null && (
            <p style={{ color: COLORS.avg }}>
              Average: <span className="font-semibold">{d.avg}%</span>
            </p>
          )}
          {d.min != null && (
            <p style={{ color: COLORS.min }}>
              Min: <span className="font-semibold">{d.min}%</span>
            </p>
          )}
          {d.max != null && (
            <p className="text-gray-600 dark:text-gray-300">
              Max: <span className="font-semibold">{d.max}%</span>
            </p>
          )}
          {d.movingAvg != null && (
            <p style={{ color: COLORS.movingAvg }}>
              Moving Avg: <span className="font-semibold">{d.movingAvg}%</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Range Selector */}
      <RangeSelector
        {...selectorProps}
        right={(
          <span className="shrink-0 text-sm text-gray-600 dark:text-gray-300">
            {stats?.totalEntries} nights with data
          </span>
        )}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Latest Avg</p>
              <p className="text-2xl font-bold" style={{ color: COLORS.avg }}>
                {stats?.latestAvg != null ? `${stats.latestAvg}%` : '--'}
              </p>
            </CardContent>
          </Card>
        )}
        {!singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Period Avg</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.overallAvg != null ? `${stats.overallAvg}%` : '--'}
              </p>
            </CardContent>
          </Card>
        )}
        {!singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Lowest Avg</p>
              <p className="text-2xl font-bold" style={{ color: COLORS.min }}>
                {stats?.lowestAvg != null ? `${stats.lowestAvg}%` : '--'}
              </p>
            </CardContent>
          </Card>
        )}
        {!singleDay && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Lowest Min</p>
              <p className="text-2xl font-bold" style={{ color: COLORS.min }}>
                {stats?.overallMin != null ? `${stats.overallMin}%` : '--'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* SpO2 Trend Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Blood Oxygen (SpO2)</CardTitle>
            <span className="text-sm text-gray-400">Normal: 95–100%</span>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={[
                  (dataMin) => Math.floor(Math.min(dataMin, 90)),
                  100,
                ]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={48}
              />
              <Tooltip content={<SpO2Tooltip />} />
              <Legend
                formatter={(value) => {
                  const map = { avg: 'Average', min: 'Nightly Min', movingAvg: 'Moving Avg' };
                  return map[value] || value;
                }}
                wrapperStyle={{ fontSize: 14 }}
              />
              <ReferenceLine
                y={95}
                stroke="#22c55e"
                strokeDasharray="3 3"
                label={{ value: '95%', position: 'insideTopLeft', fill: '#22c55e', fontSize: 14 }}
              />
              <Area
                dataKey="avg"
                stroke={COLORS.avg}
                strokeWidth={2}
                fill={COLORS.avg}
                fillOpacity={0.06}
                type="monotone"
                dot={chartData.length <= 60 ? { r: 3, fill: COLORS.avg } : false}
                connectNulls
                name="avg"
              />
              <Line
                dataKey="min"
                stroke={COLORS.min}
                strokeWidth={1.5}
                dot={chartData.length <= 60 ? { r: 2, fill: COLORS.min } : false}
                type="monotone"
                connectNulls
                name="min"
                strokeDasharray="2 2"
              />
              <Line
                dataKey="movingAvg"
                stroke={COLORS.movingAvg}
                strokeWidth={2}
                dot={false}
                name="movingAvg"
                type="monotone"
                connectNulls
                strokeDasharray="5 3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Min/Max Range Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Nightly SpO2 Range</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={[
                  (dataMin) => Math.floor(Math.min(dataMin, 88)),
                  100,
                ]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={48}
              />
              <Tooltip content={<SpO2Tooltip />} />
              <Area
                dataKey="max"
                stroke="transparent"
                fill={COLORS.range}
                fillOpacity={0.12}
                type="monotone"
                connectNulls
                name="max"
              />
              <Area
                dataKey="min"
                stroke="transparent"
                fill="#ffffff"
                fillOpacity={1}
                type="monotone"
                connectNulls
                name="minArea"
                className="dark:fill-gray-900"
              />
              <Line
                dataKey="max"
                stroke={COLORS.avg}
                strokeWidth={1.5}
                dot={false}
                type="monotone"
                connectNulls
                name="maxLine"
              />
              <Line
                dataKey="min"
                stroke={COLORS.min}
                strokeWidth={1.5}
                dot={false}
                type="monotone"
                connectNulls
                name="minLine"
              />
              <ReferenceLine y={95} stroke="#22c55e" strokeDasharray="3 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default SpO2Chart;
